import { useCallback, useMemo } from 'react'
import { useGetSettingsQuery, useUpdateSettingsMutation } from '../redux/api/customersApi'
import { useAuth } from '../auth/UseAuth'
import { ContractAnonymousViewModel } from '@ail/contracts-api'
import { OrderType } from '@ail/orders-api'

export interface GridSettings {
  rowsPerPage: number
  filtersVisible: boolean
  columns: GridColumnSettings[]
}

export interface GridColumnSettings {
  key: string
  hide?: boolean
  sortDirection?: 'ASC' | 'DESC'
  filter?: string[]
}

export interface UserSettings {
  lang: string
  iLinkGrids: Record<string, GridSettings | undefined>
  iLinkOutgoingTenant: string | undefined
  iLinkIncomingTenant: string | undefined
  iLinkOrderTypes: OrderType[]
  orderDetails: { showEmptyFields: boolean }
  supplierOrderDetails: { showEmptyFields: boolean }
  iLinkContractHistory: Record<string, ContractAnonymousViewModel[] | undefined>
}

export const DEFAULT: UserSettings = {
  lang: 'de',
  iLinkGrids: {},
  iLinkOutgoingTenant: undefined,
  iLinkOrderTypes: [],
  iLinkIncomingTenant: undefined,
  orderDetails: { showEmptyFields: true },
  supplierOrderDetails: { showEmptyFields: true },
  iLinkContractHistory: {},
}

const langs = ['en', 'de', 'fr', 'it']

export function useSettings<T extends keyof UserSettings>(key: T, options?: { remote: boolean }) {
  const { user } = useAuth()
  const remote = options ? options.remote : !!user
  const settings = useGetSettingsQuery({ remote })
  const [updateSettings] = useUpdateSettingsMutation()

  const setting = useMemo(() => {
    const saved = settings.data?.[key]
    if (!saved && key === 'lang' && user?.locale && langs.includes(user.locale)) return user.locale as UserSettings[T]
    return (saved ?? DEFAULT[key]) as UserSettings[T]
  }, [key, settings.data, user?.locale])

  const saveSetting = useCallback(
    (newSetting: UserSettings[T]) => {
      const settingsEmpty = settings.error && 'isApiException' in settings.error && settings.error.status === 404
      if (settings.data || settingsEmpty) {
        updateSettings({
          remote,
          model: {
            ...settings.data,
            lang: settings.data?.lang ?? DEFAULT.lang,
            [key]: newSetting,
          },
        })
      }
    },
    [key, remote, settings.data, settings.error, updateSettings]
  )

  return [setting, saveSetting, settings.isLoading] as const
}
